import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MenuItemsService } from '../menu-items.service';
import { MenuCategoriesService, MenuCategory } from '../../menu-categories/menu-categories.service';
import { TaxesService, Tax } from '../../taxes/taxes.service';
import { ModifierGroupsService, ModifierGroup } from '../../modifiers/modifier-groups.service';

export interface MenuItemFormDialogData {
  id?: string;
}

@Component({
  selector: 'app-menu-item-form-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
  ],
  template: `
    <div class="dialog-header">
      <h2 mat-dialog-title>
        <mat-icon>restaurant_menu</mat-icon>
        {{ isEditMode ? 'Edit Menu Item' : 'Create Menu Item' }}
      </h2>
      <button mat-icon-button type="button" (click)="onCancel()" [disabled]="loading">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <mat-dialog-content>
      <form [formGroup]="form" (ngSubmit)="onSubmit()" id="itemForm">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Name</mat-label>
          <input matInput formControlName="name" placeholder="e.g. Chicken Fried Rice" required />
          <mat-error>{{ getError('name') }}</mat-error>
        </mat-form-field>

        <div class="form-row">
          <mat-form-field appearance="outline">
            <mat-label>Category</mat-label>
            <mat-select formControlName="menu_category_id">
              <mat-option [value]="null">— None —</mat-option>
              <mat-option *ngFor="let c of categories" [value]="c.id">{{ c.name }}</mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Tax</mat-label>
            <mat-select formControlName="tax_id">
              <mat-option [value]="null">— None —</mat-option>
              <mat-option *ngFor="let t of taxes" [value]="t.id">{{ t.name }} ({{ t.rate }}{{ t.type === 'percentage' ? '%' : '' }})</mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <div class="form-row">
          <mat-form-field appearance="outline">
            <mat-label>Price</mat-label>
            <input matInput type="number" formControlName="price" min="0" step="0.01" required />
            <mat-error>{{ getError('price') }}</mat-error>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Cost</mat-label>
            <input matInput type="number" formControlName="cost" min="0" step="0.01" />
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Prep time (min)</mat-label>
            <input matInput type="number" formControlName="preparation_time" min="0" />
          </mat-form-field>
        </div>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Description</mat-label>
          <textarea matInput formControlName="description" rows="2" placeholder="Optional description"></textarea>
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Modifier groups</mat-label>
          <mat-select formControlName="modifier_group_ids" multiple>
            <mat-option *ngFor="let g of modifierGroups" [value]="g.id">{{ g.name }}</mat-option>
          </mat-select>
          <mat-hint>Attach modifier groups (e.g. Size, Extras) to this item</mat-hint>
        </mat-form-field>

        <div class="image-field">
          <div class="image-preview" *ngIf="previewUrl">
            <img [src]="previewUrl" alt="Item image" />
          </div>
          <button type="button" mat-stroked-button (click)="fileInput.click()">
            <mat-icon>image</mat-icon> {{ previewUrl ? 'Change image' : 'Upload image' }}
          </button>
          <input #fileInput type="file" accept="image/*" hidden (change)="onFileSelected($event)" />
        </div>

        <div class="form-section">
          <mat-slide-toggle formControlName="is_active" color="accent">Active</mat-slide-toggle>
        </div>
      </form>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()" [disabled]="loading">Cancel</button>
      <button mat-raised-button color="primary" form="itemForm" type="submit" [disabled]="loading || form.invalid">
        <mat-spinner *ngIf="loading" diameter="20"></mat-spinner>
        <span *ngIf="!loading">{{ isEditMode ? 'Update' : 'Create' }}</span>
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .dialog-header { display: flex; align-items: center; justify-content: space-between; }
    .dialog-header h2 { display: flex; align-items: center; gap: 8px; margin: 0; }
    mat-dialog-content { min-width: 620px; padding-top: 8px; }
    .full-width { width: 100%; }
    .form-row { display: flex; gap: 16px; }
    .form-row mat-form-field { flex: 1; }
    .form-section { margin: 8px 0; }
    .image-field { display: flex; align-items: center; gap: 16px; margin: 8px 0 16px; }
    .image-preview { width: 64px; height: 64px; border-radius: 10px; overflow: hidden; background: #f1f5f9; flex-shrink: 0; }
    .image-preview img { width: 100%; height: 100%; object-fit: cover; }
    mat-dialog-actions button mat-spinner { display: inline-block; margin-right: 8px; }
    @media (max-width: 680px) { mat-dialog-content { min-width: unset; } .form-row { flex-direction: column; gap: 0; } }
  `],
})
export class MenuItemFormDialogComponent implements OnInit {
  form: FormGroup;
  loading = false;
  isEditMode: boolean;
  id?: string;
  categories: MenuCategory[] = [];
  taxes: Tax[] = [];
  modifierGroups: ModifierGroup[] = [];
  selectedFile: File | null = null;
  previewUrl: string | null = null;

  constructor(
    private fb: FormBuilder,
    private service: MenuItemsService,
    private categoriesService: MenuCategoriesService,
    private taxesService: TaxesService,
    private modifierGroupsService: ModifierGroupsService,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<MenuItemFormDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MenuItemFormDialogData,
  ) {
    this.id = data?.id;
    this.isEditMode = !!this.id;
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      menu_category_id: [null],
      tax_id: [null],
      price: [0, [Validators.required, Validators.min(0)]],
      cost: [0, [Validators.min(0)]],
      preparation_time: [0, [Validators.min(0)]],
      description: [''],
      modifier_group_ids: [[]],
      is_active: [true],
    });
  }

  ngOnInit(): void {
    forkJoin({
      categories: this.categoriesService.getAll(),
      taxes: this.taxesService.getAll(),
      groups: this.modifierGroupsService.getAll(),
    }).subscribe({
      next: ({ categories, taxes, groups }) => {
        this.categories = categories.data ?? [];
        this.taxes = taxes.data ?? [];
        this.modifierGroups = groups.data ?? [];
        if (this.isEditMode) this.load();
      },
      error: () => {
        if (this.isEditMode) this.load();
      },
    });
  }

  private load(): void {
    if (!this.id) return;
    this.loading = true;
    this.service.get(this.id).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          const item = res.data;
          this.form.patchValue({
            name: item.name,
            menu_category_id: item.menu_category_id ?? null,
            tax_id: item.tax_id ?? null,
            price: Number(item.price),
            cost: Number(item.cost),
            preparation_time: item.preparation_time,
            description: item.description ?? '',
            modifier_group_ids: item.modifier_group_ids ?? [],
            is_active: item.is_active,
          });
          this.previewUrl = item.image ?? null;
        }
        this.loading = false;
      },
      error: () => {
        this.snackBar.open('Failed to load menu item', 'Close', { duration: 3000 });
        this.loading = false;
        this.dialogRef.close(false);
      },
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.selectedFile = file;
    if (file) {
      const reader = new FileReader();
      reader.onload = () => (this.previewUrl = reader.result as string);
      reader.readAsDataURL(file);
    }
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.loading = true;
    const payload = { ...this.form.value, image: this.selectedFile };
    const request$ = this.isEditMode && this.id
      ? this.service.update(this.id, payload)
      : this.service.create(payload);

    request$.subscribe({
      next: (res) => {
        if (res.success) {
          this.snackBar.open(`Menu item ${this.isEditMode ? 'updated' : 'created'} successfully`, 'Close', { duration: 3000 });
          this.dialogRef.close(true);
        }
        this.loading = false;
      },
      error: (error) => {
        this.snackBar.open(error.error?.message || 'Failed to save menu item', 'Close', { duration: 5000 });
        if (error.error?.errors) {
          Object.keys(error.error.errors).forEach((key) => {
            this.form.get(key)?.setErrors({ serverError: error.error.errors[key][0] });
          });
        }
        this.loading = false;
      },
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  getError(controlName: string): string {
    const control = this.form.get(controlName);
    if (!control?.errors) return '';
    if (control.errors['required']) return 'This field is required';
    if (control.errors['min']) return 'Must be zero or greater';
    if (control.errors['maxlength']) return 'Too long';
    if (control.errors['serverError']) return control.errors['serverError'];
    return 'Invalid value';
  }
}
